/**
 * Generative data service
 *
 * Thin, backend‑agnostic helpers for hydrating generative UI blocks with live
 * workspace data and executing actions triggered from those blocks.
 *
 * The concrete integrations with the Connect backend will be implemented in a
 * dedicated backend/frontend epic. Until then, these helpers keep a stable
 * contract and behave as safe no‑ops when no dataSource or intent is provided.
 */

/**
 * Hydrates a generative block with live data when a dataSource descriptor is
 * present. For now we assume the block already contains enough data to render
 * and simply echo it back.
 *
 * @param {Object} block
 * @param {import('vuex').Store} _store
 * @returns {Promise<Object>}
 */
export async function fetchBlockData(block, _store) {
  if (!block) {
    return block
  }

  // When a backend contract is available, this function can use `block.dataSource`
  // together with the Vuex store to fetch fresh data from the workspace.
  return block
}

/**
 * Executes a high‑level action triggered from a generative block. The action
 * payload is intentionally generic so that the backend/LLM can decide which
 * intents and routes to provide.
 *
 * @param {import('vue').default} vm - Vue instance providing $store / $router.
 * @param {Object} block
 * @param {Object} action
 * @returns {Promise<void>}
 */
export async function executeBlockAction(vm, block, action) {
  if (!action || !action.intent) {
    return
  }

  switch (action.intent) {
    case 'open_grid':
      // Navigate to a grid view route provided in the action payload.
      if (action.route) {
        vm.$router.push(action.route)
      } else if (action.payload && action.payload.route) {
        vm.$router.push(action.payload.route)
      }
      break
    case 'open_row':
      // Future extension point: dispatch to an existing row modal action.
      // For now this is intentionally left as a no‑op to avoid guessing
      // the concrete backend API.
      break
    default:
      // Unknown intents are ignored to keep the UI stable even if the backend
      // starts sending newer action types first.
      break
  }
}

